import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  API_KEYS_STORAGE_KEY,
  MCP_PUBLIC_URL,
  QUOTA_USERS_STORAGE_KEY,
  SINGLETON_ENDPOINT_ID,
  SINGLETON_ENDPOINT_SLUG,
  STORAGE_KEY,
  TENANT_ID,
  TENANT_NAME,
  TRACE_PIPELINE,
  normalizeMcpToolName,
  resolveCapabilityIconKey,
} from './governedMcpConstants';
import { governedMcpApi } from './governedMcpApi';
import { logGovernedMcp } from './governedMcpLogger';
import { EMPTY_QUOTA_DIRECTORY, loadQuotaDirectory } from './quotaDirectory';
import { buildEffectiveQuotaUsers, currentQuotaMonth, isNewerQuotaAssignment, isQuotaMonth, quotaRecordId, sameQuotaResource } from './quotaUsage';
import { publishCatalogService } from './publishCatalogService';
import {
  DATA_MODELS,
  DIRECTORY_GROUPS,
  DIRECTORY_USERS,
  SEED_API_KEYS,
  WORKFLOWS,
} from './governedMcpSynthetic';
import type {
  AccessPolicy,
  ActivityDecision,
  ActivityEvent,
  ConfigSection,
  ExternalMcpConnection,
  GatewayApiKey,
  GatewayFilters,
  GatewayStoreState,
  GatewayTab,
  QuotaAssignment,
  QuotaAssignmentStatus,
  QuotaDirectory,
  QuotaResourceType,
  QuotaScope,
  QuotaUsage,
  QuotaUser,
  UsageSection,
  McpClient,
  McpEndpoint,
  PublishedCapability,
  SecuritySwitches,
  ToolGroup,
  TraceStep,
} from './governedMcpTypes';

function nowIso(): string {
  return new Date().toISOString();
}

export function capabilityGroupIds(item: Pick<PublishedCapability, 'groupIds'> & { groupId?: string }): string[] {
  if (Array.isArray(item.groupIds) && item.groupIds.length) {
    return item.groupIds.filter(Boolean);
  }
  return item.groupId ? [item.groupId] : [];
}

export function createId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

const EMPTY_FILTERS: GatewayFilters = {
  activityQuery: '',
  activityDecision: 'all',
  activityEndpointId: '',
  activityClientId: '',
};

const DEFAULT_SECURITY: SecuritySwitches = {
  requireAuthenticatedUsers: true,
  requireRegisteredClients: true,
  propagateEnterpriseIdentity: true,
  enforceSelficPermissions: true,
  enableOutputGovernance: true,
  enableSensitiveDataMasking: true,
  enableCompleteAuditLogging: true,
  applyRateLimits: true,
  rateLimitPerMinute: 120,
};

function emptyRecords() {
  return {
    endpoints: [] as McpEndpoint[],
    clients: [] as McpClient[],
    policies: [] as AccessPolicy[],
    published: [] as PublishedCapability[],
    toolGroups: [] as ToolGroup[],
    apiKeys: [] as GatewayApiKey[],
    quotaAssignments: [] as QuotaAssignment[],
    quotaUsages: [] as QuotaUsage[],
    quotaUsers: [] as QuotaUser[],
    activity: [] as ActivityEvent[],
  };
}

function seedState(): GatewayStoreState {
  return {
    ...emptyRecords(),
    activeTab: 'dashboard',
    configSection: 'setup',
    usageSection: 'api-keys',
    filters: EMPTY_FILTERS,
  };
}

const CONFIG_SECTIONS: ConfigSection[] = ['setup', 'clients', 'policies'];
const USAGE_SECTIONS: UsageSection[] = ['api-keys', 'quotas', 'statistics', 'guide'];
const TOP_TABS: GatewayTab[] = ['dashboard', 'tools', 'configuration', 'api-keys', 'activity'];

function isConfigSection(value: unknown): value is ConfigSection {
  return typeof value === 'string' && CONFIG_SECTIONS.includes(value as ConfigSection);
}

function isUsageSection(value: unknown): value is UsageSection {
  return typeof value === 'string' && USAGE_SECTIONS.includes(value as UsageSection);
}

function isGatewayTab(value: unknown): value is GatewayTab {
  return typeof value === 'string' && TOP_TABS.includes(value as GatewayTab);
}

function inferSourceKind(item: PublishedCapability): PublishedCapability['sourceKind'] {
  if (item.sourceKind) return item.sourceKind;
  if (item.backedBy === 'external_mcp') return 'external_mcp';
  if (item.backedBy === 'knowledge_base') return 'knowledge_base';
  if (item.backedBy === 'workflow' || item.backedBy === 'long_running_workflow') return 'workflow';
  return 'data_model';
}

function inferMcpCapability(item: PublishedCapability): PublishedCapability['mcpCapability'] {
  if (item.mcpCapability) return item.mcpCapability;
  const hasTool = item.mcpRepresentations?.includes('tool');
  const hasResource = item.mcpRepresentations?.includes('resource');
  if (hasResource && !hasTool) return 'resource';
  return 'tool';
}

function normalizePublished(item: PublishedCapability): PublishedCapability {
  const groupIds = capabilityGroupIds(item);
  return {
    ...item,
    groupIds,
    groupId: groupIds[0] || '',
    resourceSchema: item.resourceSchema ?? '',
    promptInjectionProtection: item.promptInjectionProtection !== false,
    piiDetection: item.piiDetection !== false,
    secretsLeakageProtection: item.secretsLeakageProtection !== false,
    jailbreakProtection: item.jailbreakProtection !== false,
    mcpCapability: inferMcpCapability(item),
    sourceKind: inferSourceKind(item),
    dataModelOperations: item.dataModelOperations ?? [],
    selectedTableIds: item.selectedTableIds ?? [],
    publishScope: item.publishScope === 'data_model' || item.publishScope === 'asset' ? item.publishScope : undefined,
    assetKind: item.assetKind || '',
    assetName: item.assetName || '',
    customContext: item.customContext ?? '',
    customResourceText: item.customResourceText ?? '',
    icon: resolveCapabilityIconKey(item.icon),
    mcpToolName: normalizeMcpToolName(item.mcpToolName || ''),
  };
}

function migrateLegacyKind<T extends { capabilityKind?: string | null; backedBy?: string }>(item: T): T {
  const next = { ...item };
  if (next.capabilityKind === 'existing_mcp') next.capabilityKind = 'external_mcp';
  if (next.backedBy === 'existing_mcp') next.backedBy = 'external_mcp';
  return next;
}

function loadUiPrefs(): Pick<GatewayStoreState, 'activeTab' | 'configSection' | 'usageSection' | 'filters'> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { activeTab: 'dashboard', configSection: 'setup', usageSection: 'api-keys', filters: EMPTY_FILTERS };
    }
    const parsed = JSON.parse(raw) as Partial<GatewayStoreState>;
    const rawTab = parsed.activeTab as string | undefined;
    const resolvedTab: GatewayTab = isConfigSection(rawTab)
      ? 'configuration'
      : isGatewayTab(rawTab)
        ? rawTab
        : 'dashboard';
    const activeTab: GatewayTab = resolvedTab === 'activity' ? 'dashboard' : resolvedTab;
    const rawSection = parsed.configSection as string | undefined;
    const configSection: ConfigSection =
      rawSection === 'endpoints'
        ? 'setup'
        : isConfigSection(rawSection)
          ? rawSection
          : rawTab === 'endpoints'
            ? 'setup'
            : isConfigSection(rawTab)
              ? rawTab
              : 'setup';
    const storedUsage = (parsed as { usageSection?: unknown }).usageSection;
    const rawUsage = typeof storedUsage === 'string' ? storedUsage : undefined;
    const usageSection: UsageSection =
      rawUsage === 'governance'
        ? 'statistics'
        : isUsageSection(rawUsage)
          ? rawUsage
          : 'api-keys';
    if (rawUsage === 'governance') {
      logGovernedMcp('app', 'usage_section_migrated', { from: 'governance', to: 'statistics' });
    }
    logGovernedMcp('app', 'store_prefs_hydrated', { activeTab, configSection, usageSection });
    return {
      activeTab,
      configSection,
      usageSection,
      filters: { ...EMPTY_FILTERS, ...(parsed.filters ?? {}) },
    };
  } catch (err) {
    logGovernedMcp('error', 'store_prefs_hydrate_failed', {
      message: err instanceof Error ? err.message : String(err),
    });
    return { activeTab: 'dashboard', configSection: 'setup', usageSection: 'api-keys', filters: EMPTY_FILTERS };
  }
}

function normalizeApiKey(item: GatewayApiKey): GatewayApiKey {
  return {
    ...item,
    kind: item.kind === 'user' ? 'user' : item.kind === 'llm' ? 'llm' : 'key',
    name: item.name || '',
    description: item.description || '',
    secret: item.secret || '',
    tokenJti: item.tokenJti || '',
    expiresAt: item.expiresAt || '',
    username: item.username || '',
    userId: item.userId || '',
    email: item.email || '',
    tenantId: item.tenantId || TENANT_ID,
    details: item.details || '',
    clientId: item.clientId || '',
    endpointId: SINGLETON_ENDPOINT_ID,
    status: item.status === 'revoked' ? 'revoked' : 'active',
    lastUsedAt: item.lastUsedAt ?? null,
    createdBy: item.createdBy || '',
  };
}

function loadLocalApiKeys(): GatewayApiKey[] {
  try {
    const raw = sessionStorage.getItem(API_KEYS_STORAGE_KEY);
    if (!raw) return SEED_API_KEYS.map(normalizeApiKey);
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return SEED_API_KEYS.map(normalizeApiKey);
    return parsed.map((row) => normalizeApiKey(row as GatewayApiKey));
  } catch (err) {
    logGovernedMcp('warning', 'api_keys_hydrate_failed', {
      message: err instanceof Error ? err.message : String(err),
    });
    return SEED_API_KEYS.map(normalizeApiKey);
  }
}

function normalizeQuotaAssignment(item: QuotaAssignment): QuotaAssignment {
  const scope = item.scope === 'role' || item.scope === 'department' || item.scope === 'user' ? item.scope : 'user';
  const month = isQuotaMonth(item.month || '') ? item.month : '';
  const resourceType =
    item.resourceType === 'mcp_tool' || item.resourceType === 'llm_gateway' || item.resourceType === 'client'
      ? item.resourceType
      : item.clientId?.startsWith('gw-')
        ? 'llm_gateway'
        : undefined;
  return {
    id: item.id || (month ? quotaRecordId(scope, item.scopeId, item.clientId, item.resourceId || item.clientId, month) : ''),
    scope,
    scopeId: item.scopeId || '',
    clientId: item.clientId || '',
    resourceId: item.resourceId || item.clientId || '',
    month,
    tokenLimit: Number.isFinite(item.tokenLimit) ? Math.max(0, Math.round(item.tokenLimit)) : 0,
    status: item.status === 'inactive' ? 'inactive' : 'active',
    name: item.name || '',
    renewsMonthly: Boolean(item.renewsMonthly),
    ...(resourceType ? { resourceType } : {}),
    version: Number.isFinite(item.version) ? Math.max(1, Math.round(item.version || 1)) : 1,
    updatedAt: item.updatedAt || nowIso(),
    hasInferenceKey: Boolean(item.hasInferenceKey),
    inferenceKeyLast4: item.inferenceKeyLast4 || null,
    inferenceKeyIssuedAt: item.inferenceKeyIssuedAt || null,
    inferenceKeyExpiresAt: item.inferenceKeyExpiresAt || null,
    inferenceKeyUserId: item.inferenceKeyUserId || null,
  };
}

function normalizeQuotaUsage(item: QuotaUsage): QuotaUsage {
  return {
    id: item.id || '',
    userId: item.userId || '',
    clientId: item.clientId || '',
    tokensUsed: Number.isFinite(item.tokensUsed) ? Math.max(0, Math.round(item.tokensUsed)) : 0,
    dailyUsage: Array.isArray(item.dailyUsage) ? item.dailyUsage : [],
    byModel: Array.isArray(item.byModel) ? item.byModel : [],
    updatedAt: item.updatedAt || nowIso(),
  };
}

function loadState(): GatewayStoreState {
  return { ...seedState(), ...loadUiPrefs(), apiKeys: loadLocalApiKeys() };
}

function asStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)) : [];
}

function normalizeEndpoint(item: McpEndpoint): McpEndpoint {
  return {
    ...item,
    security: { ...DEFAULT_SECURITY, ...(item.security || {}) },
    oidc: {
      issuerUrl: item.oidc?.issuerUrl || '',
      clientId: item.oidc?.clientId || '',
      scopes: item.oidc?.scopes || '',
      audience: item.oidc?.audience || '',
    },
    requestVolume: Number(item.requestVolume) || 0,
    credentialVersion: Number(item.credentialVersion) || 1,
  };
}

export function createSingletonEndpoint(source?: McpEndpoint): McpEndpoint {
  const now = nowIso();
  const base = source ? normalizeEndpoint(source) : undefined;
  return {
    id: SINGLETON_ENDPOINT_ID,
    name: base?.name || 'Selfic MCP',
    description: base?.description || 'Governed MCP host for this tenant.',
    environment: 'production',
    slug: SINGLETON_ENDPOINT_SLUG,
    url: MCP_PUBLIC_URL,
    transport: 'streamable-http',
    compatibilitySse: base?.compatibilitySse ?? true,
    authMethod: base?.authMethod || 'universal_token',
    identityProvider: base?.identityProvider || 'selfic_sso',
    oidc: base?.oidc || { issuerUrl: '', clientId: '', scopes: '', audience: '' },
    enabled: base?.enabled !== false,
    security: { ...DEFAULT_SECURITY, ...(base?.security || {}) },
    credentialVersion: base?.credentialVersion || 1,
    lastRotatedAt: base?.lastRotatedAt || now,
    requestVolume: base?.requestVolume || 0,
    lastActivityAt: base?.lastActivityAt || null,
    createdAt: base?.createdAt || now,
    updatedAt: now,
    createdBy: base?.createdBy || 'Selfic',
  };
}

function bindRecordsToSingleton(input: ReturnType<typeof emptyRecords>): {
  records: ReturnType<typeof emptyRecords>;
  changed: boolean;
} {
  const preferred =
    input.endpoints.find((item) => item.id === SINGLETON_ENDPOINT_ID) ||
    input.endpoints.find((item) => item.slug === SINGLETON_ENDPOINT_SLUG) ||
    input.endpoints[0];
  const singleton = createSingletonEndpoint(preferred);
  const changed =
    input.endpoints.length !== 1 ||
    input.endpoints[0]?.id !== SINGLETON_ENDPOINT_ID ||
    input.endpoints[0]?.url !== MCP_PUBLIC_URL ||
    input.endpoints[0]?.slug !== SINGLETON_ENDPOINT_SLUG ||
    input.clients.some((item) => item.endpointId !== SINGLETON_ENDPOINT_ID) ||
    input.published.some((item) => item.endpointId !== SINGLETON_ENDPOINT_ID) ||
    input.apiKeys.some((item) => item.endpointId !== SINGLETON_ENDPOINT_ID) ||
    input.policies.some((item) => item.endpointIds.length !== 1 || item.endpointIds[0] !== SINGLETON_ENDPOINT_ID);
  return {
    changed,
    records: {
      ...input,
      endpoints: [singleton],
      clients: input.clients.map((item) => ({ ...item, endpointId: SINGLETON_ENDPOINT_ID })),
      published: input.published.map((item) => ({ ...item, endpointId: SINGLETON_ENDPOINT_ID })),
      apiKeys: input.apiKeys.map((item) => ({ ...item, endpointId: SINGLETON_ENDPOINT_ID })),
      policies: input.policies.map((item) => ({
        ...item,
        endpointIds: [SINGLETON_ENDPOINT_ID],
      })),
      activity: input.activity.map((item) => ({
        ...item,
        endpointId: SINGLETON_ENDPOINT_ID,
        endpointName: singleton.name,
      })),
    },
  };
}

function normalizeClient(item: McpClient): McpClient {
  return {
    ...item,
    allowedUserIds: asStringList(item.allowedUserIds),
    allowedGroupIds: asStringList(item.allowedGroupIds),
    workspaceIds: asStringList(item.workspaceIds),
    authOption: item.authOption === 'oauth' || item.authOption === 'api_key' ? item.authOption : '',
  };
}

function normalizePolicy(item: AccessPolicy): AccessPolicy {
  return {
    ...item,
    userIds: asStringList(item.userIds),
    groupIds: asStringList(item.groupIds),
    clientIds: asStringList(item.clientIds),
    endpointIds: asStringList(item.endpointIds),
    publishedCapabilityIds: asStringList(item.publishedCapabilityIds),
    environments: Array.isArray(item.environments) ? item.environments : [],
  };
}

function normalizeActivity(item: ActivityEvent): ActivityEvent {
  return migrateLegacyKind({
    ...item,
    groupNames: asStringList(item.groupNames),
    roles: asStringList(item.roles),
    representations: Array.isArray(item.representations) ? item.representations : [],
    policiesEvaluated: asStringList(item.policiesEvaluated),
    dataSources: asStringList(item.dataSources),
    classificationsEncountered: Array.isArray(item.classificationsEncountered)
      ? item.classificationsEncountered
      : [],
    fieldsMasked: asStringList(item.fieldsMasked),
    trace: Array.isArray(item.trace) ? item.trace : [],
  });
}

type StoreAction =
  | { type: 'set_tab'; tab: GatewayTab }
  | { type: 'set_config_section'; section: ConfigSection }
  | { type: 'set_usage_section'; section: UsageSection }
  | { type: 'open_configuration'; section: ConfigSection }
  | { type: 'set_filters'; filters: Partial<GatewayFilters> }
  | { type: 'upsert_endpoint'; endpoint: McpEndpoint }
  | { type: 'delete_endpoint'; id: string }
  | { type: 'upsert_client'; client: McpClient }
  | { type: 'delete_client'; id: string }
  | { type: 'upsert_policy'; policy: AccessPolicy }
  | { type: 'delete_policy'; id: string }
  | { type: 'upsert_published'; item: PublishedCapability }
  | { type: 'delete_published'; id: string }
  | { type: 'upsert_tool_group'; group: ToolGroup }
  | { type: 'delete_tool_group'; id: string }
  | { type: 'upsert_api_key'; item: GatewayApiKey }
  | { type: 'delete_api_key'; id: string }
  | { type: 'upsert_quota_assignment'; item: QuotaAssignment }
  | { type: 'delete_quota_assignment'; id: string }
  | { type: 'hydrate_quota_bundle'; assignments: QuotaAssignment[]; usages: QuotaUsage[] }
  | { type: 'prepend_activity'; event: ActivityEvent }
  | { type: 'hydrate_records'; payload: ReturnType<typeof emptyRecords> }
  | { type: 'reset' };

function reducer(state: GatewayStoreState, action: StoreAction): GatewayStoreState {
  switch (action.type) {
    case 'set_tab':
      return { ...state, activeTab: action.tab === 'activity' ? 'dashboard' : action.tab };
    case 'set_config_section':
      return { ...state, configSection: action.section };
    case 'set_usage_section':
      return { ...state, usageSection: action.section };
    case 'open_configuration':
      return { ...state, activeTab: 'configuration', configSection: action.section };
    case 'set_filters':
      return { ...state, filters: { ...state.filters, ...action.filters } };
    case 'upsert_endpoint': {
      const exists = state.endpoints.some((item) => item.id === action.endpoint.id);
      return {
        ...state,
        endpoints: exists
          ? state.endpoints.map((item) => (item.id === action.endpoint.id ? action.endpoint : item))
          : [action.endpoint, ...state.endpoints],
      };
    }
    case 'delete_endpoint':
      return { ...state, endpoints: state.endpoints.filter((item) => item.id !== action.id) };
    case 'upsert_client': {
      const exists = state.clients.some((item) => item.id === action.client.id);
      return {
        ...state,
        clients: exists
          ? state.clients.map((item) => (item.id === action.client.id ? action.client : item))
          : [action.client, ...state.clients],
      };
    }
    case 'delete_client':
      return { ...state, clients: state.clients.filter((item) => item.id !== action.id) };
    case 'upsert_policy': {
      const exists = state.policies.some((item) => item.id === action.policy.id);
      return {
        ...state,
        policies: exists
          ? state.policies.map((item) => (item.id === action.policy.id ? action.policy : item))
          : [action.policy, ...state.policies],
      };
    }
    case 'delete_policy':
      return { ...state, policies: state.policies.filter((item) => item.id !== action.id) };
    case 'upsert_published': {
      const exists = state.published.some((item) => item.id === action.item.id);
      return {
        ...state,
        published: exists
          ? state.published.map((item) => (item.id === action.item.id ? action.item : item))
          : [action.item, ...state.published],
      };
    }
    case 'delete_published':
      return { ...state, published: state.published.filter((item) => item.id !== action.id) };
    case 'upsert_tool_group': {
      const exists = state.toolGroups.some((item) => item.id === action.group.id);
      return {
        ...state,
        toolGroups: exists
          ? state.toolGroups.map((item) => (item.id === action.group.id ? action.group : item))
          : [...state.toolGroups, action.group],
      };
    }
    case 'delete_tool_group':
      return {
        ...state,
        toolGroups: state.toolGroups.filter((item) => item.id !== action.id),
        published: state.published.map((item) => {
          const groupIds = capabilityGroupIds(item).filter((id) => id !== action.id);
          return { ...item, groupIds, groupId: groupIds[0] || '' };
        }),
      };
    case 'upsert_api_key': {
      const exists = state.apiKeys.some((item) => item.id === action.item.id);
      return {
        ...state,
        apiKeys: exists
          ? state.apiKeys.map((item) => (item.id === action.item.id ? action.item : item))
          : [action.item, ...state.apiKeys],
      };
    }
    case 'delete_api_key':
      return { ...state, apiKeys: state.apiKeys.filter((item) => item.id !== action.id) };
    case 'upsert_quota_assignment': {
      const exists = state.quotaAssignments.some((item) => item.id === action.item.id);
      return {
        ...state,
        quotaAssignments: exists
          ? state.quotaAssignments.map((item) => (item.id === action.item.id ? action.item : item))
          : [action.item, ...state.quotaAssignments],
      };
    }
    case 'delete_quota_assignment':
      return { ...state, quotaAssignments: state.quotaAssignments.filter((item) => item.id !== action.id) };
    case 'hydrate_quota_bundle':
      return { ...state, quotaAssignments: action.assignments, quotaUsages: action.usages };
    case 'prepend_activity':
      return { ...state, activity: [action.event, ...state.activity] };
    case 'hydrate_records':
      return { ...state, ...action.payload };
    case 'reset':
      return { ...state, ...emptyRecords() };
    default:
      return state;
  }
}

function buildTrace(decision: ActivityDecision, reasons: Partial<Record<string, string>>): TraceStep[] {
  const pipeline = [...TRACE_PIPELINE];
  const stopAt =
    decision === 'failed'
      ? 'Authentication'
      : decision === 'denied'
        ? 'Authorization'
        : decision === 'approval_required'
          ? 'Policy Evaluation'
          : null;

  return pipeline.map((label, index) => {
    let status: TraceStep['status'] = 'passed';
    let reason = reasons[label] || `${label} completed. Selfic remains the enforcement boundary.`;
    if (stopAt && pipeline.indexOf(stopAt) < index) {
      status = label === 'MCP Response' ? 'passed' : 'skipped';
      reason = reasons[label] || 'Skipped because a prior governance step blocked independent execution.';
    }
    if (label === stopAt) {
      status = decision === 'approval_required' ? 'blocked' : 'failed';
      reason = reasons[label] || reason;
    }
    if (decision === 'approval_required' && label === 'Execution') {
      status = 'blocked';
      reason = reasons.Execution || 'Execution held until a human approves.';
    }
    return {
      id: `trace-${index}`,
      label,
      status,
      reason,
      at: new Date(Date.now() + index).toISOString(),
    };
  });
}

interface GatewayContextValue {
  state: GatewayStoreState;
  ready: boolean;
  loading: boolean;
  loadError: string | null;
  persistError: string | null;
  reload: () => Promise<void>;
  setTab: (tab: GatewayTab) => void;
  setConfigSection: (section: ConfigSection) => void;
  setUsageSection: (section: UsageSection) => void;
  openConfiguration: (section: ConfigSection) => void;
  setFilters: (filters: Partial<GatewayFilters>) => void;
  saveEndpoint: (endpoint: McpEndpoint) => void;
  toggleEndpoint: (id: string) => void;
  rotateCredentials: (id: string) => void;
  deleteEndpoint: (id: string) => { ok: true } | { ok: false; reason: string };
  saveClient: (client: McpClient) => void;
  setClientStatus: (id: string, status: McpClient['status']) => void;
  deleteClient: (id: string) => void;
  savePolicy: (policy: AccessPolicy) => void;
  deletePolicy: (id: string) => void;
  savePublished: (item: PublishedCapability) => void;
  deletePublished: (id: string) => void;
  saveToolGroup: (group: ToolGroup) => void;
  deleteToolGroup: (id: string) => void;
  saveApiKey: (item: GatewayApiKey) => { ok: true } | { ok: false; reason: string };
  deleteApiKey: (id: string) => void;
  setQuotaAssignment: (
    scope: QuotaScope,
    scopeId: string,
    clientId: string,
    tokenLimit: number,
    name?: string,
    month?: string,
    status?: QuotaAssignmentStatus,
    renewsMonthly?: boolean,
    resourceType?: QuotaResourceType,
    resourceId?: string
  ) => { ok: true } | { ok: false; reason: string };
  deleteQuotaAssignment: (id: string) => { ok: true } | { ok: false; reason: string };
  issueInferenceKey: (input: {
    quotaId: string;
    userId?: string;
    email?: string;
    name?: string;
    roleId?: string;
    departmentId?: string;
  }) => Promise<{ token: string; expiresAt: string; last4: string; baseUrl: string; model: string }>;
  quotaDirectory: QuotaDirectory;
  recordTestConnection: (endpointId: string, clientId?: string) => ActivityEvent;
  simulateInvocation: (input: {
    endpointId: string;
    clientId: string;
    publishedId: string;
    userId: string;
    operation?: ActivityEvent['operation'];
  }) => ActivityEvent;
  resetDemo: () => void;
  catalogs: {
    users: typeof DIRECTORY_USERS;
    groups: typeof DIRECTORY_GROUPS;
    dataModels: typeof DATA_MODELS;
    workflows: typeof WORKFLOWS;
    externalMcp: ExternalMcpConnection[];
  };
}

const GatewayContext = createContext<GatewayContextValue | null>(null);

export function GovernedMcpProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [persistError, setPersistError] = useState<string | null>(null);
  const [externalMcp, setExternalMcp] = useState<ExternalMcpConnection[]>([]);
  const [quotaDirectory, setQuotaDirectory] = useState<QuotaDirectory>(EMPTY_QUOTA_DIRECTORY);
  const reloadRef = useRef<() => Promise<void>>(async () => undefined);
  const localKeysSyncedRef = useRef(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          activeTab: state.activeTab,
          configSection: state.configSection,
          usageSection: state.usageSection,
          filters: state.filters,
        })
      );
    } catch (err) {
      logGovernedMcp('warning', 'store_prefs_persist_failed', {
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [state.activeTab, state.configSection, state.usageSection, state.filters]);

  useEffect(() => {
    try {
      sessionStorage.setItem(API_KEYS_STORAGE_KEY, JSON.stringify(state.apiKeys));
    } catch (err) {
      logGovernedMcp('warning', 'api_keys_persist_failed', {
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [state.apiKeys]);

  useEffect(() => {
    try {
      sessionStorage.setItem(QUOTA_USERS_STORAGE_KEY, JSON.stringify(state.quotaUsers));
    } catch (err) {
      logGovernedMcp('warning', 'quota_users_persist_failed', {
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [state.quotaUsers]);

  const applyRemote = useCallback((remote: ReturnType<typeof emptyRecords>) => {
    const { records, changed } = bindRecordsToSingleton({
      endpoints: remote.endpoints.map(normalizeEndpoint),
      clients: remote.clients.map(normalizeClient),
      policies: remote.policies.map(normalizePolicy),
      published: remote.published.map(migrateLegacyKind).map(normalizePublished),
      toolGroups: remote.toolGroups,
      apiKeys: (remote.apiKeys ?? []).map(normalizeApiKey),
      quotaAssignments: (remote.quotaAssignments ?? []).map(normalizeQuotaAssignment).filter((item) => item.month),
      quotaUsages: (remote.quotaUsages ?? []).map(normalizeQuotaUsage),
      quotaUsers: [],
      activity: remote.activity.map(normalizeActivity),
    });
    dispatch({ type: 'hydrate_records', payload: records });
    return { singleton: records.endpoints[0], changed };
  }, []);

  const refreshQuotas = useCallback(async () => {
    const bundle = await governedMcpApi.listQuotas();
    dispatch({
      type: 'hydrate_quota_bundle',
      assignments: bundle.assignments.map(normalizeQuotaAssignment).filter((item) => item.month),
      usages: bundle.usages.map(normalizeQuotaUsage),
    });
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      let remote = await governedMcpApi.getState();
      if (!localKeysSyncedRef.current && remote.apiKeys.length === 0) {
        const local = loadLocalApiKeys().filter((item) => item.userId.trim() && item.clientId.trim());
        localKeysSyncedRef.current = true;
        if (local.length) {
          logGovernedMcp('app', 'api_keys_local_sync', { count: local.length });
          const synced = await Promise.allSettled(local.map((item) => governedMcpApi.upsertApiKey(item)));
          const failed = synced.filter((item) => item.status === 'rejected').length;
          if (failed) {
            logGovernedMcp('warning', 'api_keys_local_sync_partial', { failed, total: local.length });
          }
          remote = await governedMcpApi.getState();
        }
      } else {
        localKeysSyncedRef.current = true;
      }
      const { singleton, changed } = applyRemote(remote);
      setLoadError(null);
      setReady(true);
      logGovernedMcp('app', 'store_remote_hydrated', {
        endpoints: 1,
        published: remote.published.length,
        apiKeys: remote.apiKeys.length,
        quotaAssignments: remote.quotaAssignments.length,
        singletonBound: changed,
      });
      if (changed && singleton) {
        void governedMcpApi.upsertEndpoint(singleton).catch((persistErr) => {
          const persistMessage = persistErr instanceof Error ? persistErr.message : String(persistErr);
          logGovernedMcp('warning', 'singleton_endpoint_persist_failed', { message: persistMessage });
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load governed MCP state';
      setLoadError(message);
      logGovernedMcp('error', 'store_remote_hydrate_failed', { message });
    } finally {
      setLoading(false);
    }
  }, [applyRemote]);

  reloadRef.current = reload;

  const persistLater = useCallback((label: string, run: () => Promise<unknown>) => {
    void run()
      .then(() => setPersistError(null))
      .catch(async (err) => {
        const message = err instanceof Error ? err.message : String(err);
        logGovernedMcp('error', `${label}_persist_failed`, { message });
        setPersistError(message);
        await reloadRef.current();
      });
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    void publishCatalogService
      .listExternalMcp()
      .then((rows) => {
        if (!cancelled) setExternalMcp(rows);
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : String(err);
        logGovernedMcp('warning', 'store_mcp_catalog_failed', { message });
        if (!cancelled) setExternalMcp([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void loadQuotaDirectory().then((next) => {
      if (!cancelled) setQuotaDirectory(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const setTab = useCallback((tab: GatewayTab) => {
    logGovernedMcp('app', 'tab_change', { tab });
    dispatch({ type: 'set_tab', tab });
  }, []);

  const setConfigSection = useCallback((section: ConfigSection) => {
    logGovernedMcp('app', 'config_section_change', { section });
    dispatch({ type: 'set_config_section', section });
  }, []);

  const setUsageSection = useCallback((section: UsageSection) => {
    logGovernedMcp('app', 'usage_section_change', { section });
    dispatch({ type: 'set_usage_section', section });
  }, []);

  const openConfiguration = useCallback((section: ConfigSection) => {
    logGovernedMcp('app', 'open_configuration', { section });
    dispatch({ type: 'open_configuration', section });
  }, []);

  const setFilters = useCallback((filters: Partial<GatewayFilters>) => {
    dispatch({ type: 'set_filters', filters });
  }, []);

  const saveEndpoint = useCallback((endpoint: McpEndpoint) => {
    const next = createSingletonEndpoint({ ...endpoint, id: SINGLETON_ENDPOINT_ID });
    logGovernedMcp('app', 'endpoint_saved', { id: next.id, slug: next.slug });
    dispatch({ type: 'upsert_endpoint', endpoint: next });
    persistLater('endpoint_saved', () => governedMcpApi.upsertEndpoint(next));
  }, [persistLater]);

  const toggleEndpoint = useCallback(
    (id: string) => {
      const current = state.endpoints.find((item) => item.id === id);
      if (!current) {
        logGovernedMcp('warning', 'endpoint_toggle_missing', { id });
        return;
      }
      logGovernedMcp('app', current.enabled ? 'endpoint_disabled' : 'endpoint_enabled', { id });
      const next = { ...current, enabled: !current.enabled, updatedAt: nowIso() };
      dispatch({ type: 'upsert_endpoint', endpoint: next });
      persistLater('endpoint_toggled', () => governedMcpApi.upsertEndpoint(next));
    },
    [persistLater, state.endpoints]
  );

  const rotateCredentials = useCallback(
    (id: string) => {
      const current = state.endpoints.find((item) => item.id === id);
      if (!current) return;
      logGovernedMcp('app', 'endpoint_credentials_rotated', { id, version: current.credentialVersion + 1 });
      const next = {
        ...current,
        credentialVersion: current.credentialVersion + 1,
        lastRotatedAt: nowIso(),
        updatedAt: nowIso(),
      };
      dispatch({ type: 'upsert_endpoint', endpoint: next });
      persistLater('endpoint_rotated', () => governedMcpApi.upsertEndpoint(next));
    },
    [persistLater, state.endpoints]
  );

  const deleteEndpoint = useCallback(
    (id: string): { ok: true } | { ok: false; reason: string } => {
      if (id === SINGLETON_ENDPOINT_ID || state.endpoints.length <= 1) {
        logGovernedMcp('warning', 'endpoint_delete_blocked', { id, reason: 'singleton' });
        return { ok: false, reason: 'The MCP host cannot be deleted.' };
      }
      const clientCount = state.clients.filter((item) => item.endpointId === id).length;
      const policyCount = state.policies.filter((item) => item.endpointIds.includes(id)).length;
      const publishedCount = state.published.filter((item) => item.endpointId === id).length;
      if (clientCount || policyCount || publishedCount) {
        const reason = `Cannot delete: ${clientCount} client(s), ${policyCount} policy(ies), and ${publishedCount} published capability(ies) still depend on this endpoint.`;
        logGovernedMcp('warning', 'endpoint_delete_blocked', { id, clientCount, policyCount, publishedCount });
        return { ok: false, reason };
      }
      logGovernedMcp('app', 'endpoint_deleted', { id });
      dispatch({ type: 'delete_endpoint', id });
      persistLater('endpoint_deleted', () => governedMcpApi.deleteEndpoint(id));
      return { ok: true };
    },
    [persistLater, state.clients, state.policies, state.published]
  );

  const saveClient = useCallback((client: McpClient) => {
    const next = { ...client, endpointId: SINGLETON_ENDPOINT_ID, updatedAt: nowIso() };
    logGovernedMcp('app', 'client_saved', { id: client.id, endpointId: next.endpointId });
    dispatch({ type: 'upsert_client', client: next });
    persistLater('client_saved', () => governedMcpApi.upsertClient(next));
  }, [persistLater]);

  const setClientStatus = useCallback(
    (id: string, status: McpClient['status']) => {
      const current = state.clients.find((item) => item.id === id);
      if (!current) return;
      logGovernedMcp('app', 'client_status', { id, status });
      const next = { ...current, status, updatedAt: nowIso() };
      dispatch({ type: 'upsert_client', client: next });
      persistLater('client_status', () => governedMcpApi.upsertClient(next));
    },
    [persistLater, state.clients]
  );

  const deleteClient = useCallback((id: string) => {
    logGovernedMcp('app', 'client_deleted', { id });
    dispatch({ type: 'delete_client', id });
    persistLater('client_deleted', () => governedMcpApi.deleteClient(id));
  }, [persistLater]);

  const savePolicy = useCallback((policy: AccessPolicy) => {
    const next = {
      ...policy,
      endpointIds: [SINGLETON_ENDPOINT_ID],
      updatedAt: nowIso(),
    };
    logGovernedMcp('app', 'policy_saved', { id: policy.id, effect: policy.effect });
    dispatch({ type: 'upsert_policy', policy: next });
    persistLater('policy_saved', () => governedMcpApi.upsertPolicy(next));
  }, [persistLater]);

  const deletePolicy = useCallback((id: string) => {
    logGovernedMcp('app', 'policy_deleted', { id });
    dispatch({ type: 'delete_policy', id });
    persistLater('policy_deleted', () => governedMcpApi.deletePolicy(id));
  }, [persistLater]);

  const savePublished = useCallback((item: PublishedCapability) => {
    const next = normalizePublished({ ...item, endpointId: SINGLETON_ENDPOINT_ID, updatedAt: nowIso() });
    logGovernedMcp('app', 'capability_published', {
      id: next.id,
      kind: next.capabilityKind,
      representations: next.mcpRepresentations,
      groupIds: next.groupIds,
    });
    dispatch({ type: 'upsert_published', item: next });
    persistLater('capability_published', () => governedMcpApi.upsertPublished(next));
  }, [persistLater]);

  const deletePublished = useCallback((id: string) => {
    logGovernedMcp('app', 'capability_unpublished', { id });
    dispatch({ type: 'delete_published', id });
    persistLater('capability_unpublished', () => governedMcpApi.deletePublished(id));
  }, [persistLater]);

  const saveToolGroup = useCallback((group: ToolGroup) => {
    const next = { ...group, updatedAt: nowIso() };
    logGovernedMcp('app', 'tool_group_saved', { id: group.id, name: group.name });
    dispatch({ type: 'upsert_tool_group', group: next });
    persistLater('tool_group_saved', () => governedMcpApi.upsertToolGroup(next));
  }, [persistLater]);

  const deleteToolGroup = useCallback((id: string) => {
    logGovernedMcp('app', 'tool_group_deleted', { id });
    dispatch({ type: 'delete_tool_group', id });
    persistLater('tool_group_deleted', () => governedMcpApi.deleteToolGroup(id));
  }, [persistLater]);

  const saveApiKey = useCallback((item: GatewayApiKey): { ok: true } | { ok: false; reason: string } => {
    const next = normalizeApiKey({ ...item, endpointId: SINGLETON_ENDPOINT_ID, updatedAt: nowIso() });
    if (!next.userId.trim()) {
      logGovernedMcp('warning', 'api_key_save_blocked', { id: next.id, reason: 'missing_user' });
      return { ok: false, reason: 'Select a user from the directory.' };
    }
    if (!next.clientId.trim()) {
      logGovernedMcp('warning', 'api_key_save_blocked', { id: next.id, reason: 'missing_client' });
      return { ok: false, reason: 'Select a registered AI client.' };
    }
    const duplicate = state.apiKeys.find(
      (row) =>
        row.id !== next.id &&
        row.kind !== 'llm' &&
        next.kind !== 'llm' &&
        Boolean(row.clientId) &&
        row.userId === next.userId &&
        row.clientId === next.clientId
    );
    if (duplicate) {
      const clientName = state.clients.find((client) => client.id === next.clientId)?.name || 'this client';
      logGovernedMcp('warning', 'api_key_duplicate_blocked', {
        id: next.id,
        existingId: duplicate.id,
        userId: next.userId,
        clientId: next.clientId,
      });
      return { ok: false, reason: `This user already has a token for ${clientName}. Open that key to reissue.` };
    }
    logGovernedMcp('app', 'api_key_saved', { id: next.id, kind: next.kind, clientId: next.clientId, userId: next.userId });
    dispatch({ type: 'upsert_api_key', item: next });
    persistLater('api_key_saved', async () => {
      await governedMcpApi.upsertApiKey(next);
      await refreshQuotas();
    });
    return { ok: true };
  }, [persistLater, refreshQuotas, state.apiKeys, state.clients]);

  const issueInferenceKey = useCallback(
    async (input: {
      quotaId: string;
      userId?: string;
      email?: string;
      name?: string;
      roleId?: string;
      departmentId?: string;
    }) => {
      logGovernedMcp('app', 'inference_key_issue', { quotaId: input.quotaId, userId: input.userId || '' });
      const issued = await governedMcpApi.issueInferenceKey(input);
      try {
        const remote = await governedMcpApi.getState();
        applyRemote({
          endpoints: remote.endpoints,
          clients: remote.clients,
          policies: remote.policies,
          published: remote.published,
          toolGroups: remote.toolGroups,
          apiKeys: remote.apiKeys,
          quotaAssignments: remote.quotaAssignments,
          quotaUsages: remote.quotaUsages,
          quotaUsers: remote.quotaUsers,
          activity: remote.activity,
        });
      } catch (error) {
        logGovernedMcp('warning', 'inference_key_hydrate_failed', {
          quotaId: input.quotaId,
          message: error instanceof Error ? error.message : String(error),
        });
      }
      return issued;
    },
    [applyRemote]
  );

  const deleteApiKey = useCallback((id: string) => {
    logGovernedMcp('app', 'api_key_deleted', { id });
    dispatch({ type: 'delete_api_key', id });
    persistLater('api_key_deleted', async () => {
      await governedMcpApi.deleteApiKey(id);
      await refreshQuotas();
    });
  }, [persistLater, refreshQuotas]);

  const setQuotaAssignment = useCallback(
    (
      scope: QuotaScope,
      scopeId: string,
      clientId: string,
      tokenLimit: number,
      name?: string,
      month?: string,
      status?: QuotaAssignmentStatus,
      renewsMonthly?: boolean,
      resourceType?: QuotaResourceType,
      resourceId?: string
    ): { ok: true } | { ok: false; reason: string } => {
      if (!scopeId.trim()) {
        return { ok: false, reason: 'A department, role, or user is required.' };
      }
      if (!clientId.trim()) {
        return { ok: false, reason: 'Select a registered AI client.' };
      }
      const requestedResource = (resourceId || (resourceType === 'client' || !resourceType ? clientId : '')).trim();
      if (!requestedResource) {
        return { ok: false, reason: 'Quota resource id is required.' };
      }
      const period = (month || '').trim();
      if (!isQuotaMonth(period)) {
        return { ok: false, reason: 'Select a month.' };
      }
      if (!Number.isFinite(tokenLimit) || tokenLimit < 0) {
        return { ok: false, reason: 'Token limit must be zero or a positive integer.' };
      }
      const nextType = resourceType || 'client';
      const existing = state.quotaAssignments.reduce<QuotaAssignment | undefined>((latest, item) => {
        if (
          item.scope !== scope ||
          item.scopeId.trim() !== scopeId.trim() ||
          item.clientId.trim() !== clientId.trim() ||
          item.month !== period ||
          (item.resourceType || 'client') !== nextType ||
          !sameQuotaResource(item.resourceId || item.clientId, requestedResource, state.published)
        ) {
          return latest;
        }
        if (!latest || isNewerQuotaAssignment(item, latest)) return item;
        return latest;
      }, undefined);
      const persistedResource = (existing?.resourceId || requestedResource).trim();
      const next = normalizeQuotaAssignment({
        id: existing?.id || quotaRecordId(scope, scopeId, clientId, persistedResource, period),
        scope,
        scopeId: scopeId.trim(),
        clientId: clientId.trim(),
        resourceId: persistedResource,
        month: period,
        tokenLimit: Math.round(tokenLimit),
        status: status === 'inactive' ? 'inactive' : 'active',
        name: name || existing?.name || '',
        renewsMonthly: Boolean(renewsMonthly),
        resourceType: nextType,
        version: (existing?.version || 0) + 1,
        updatedAt: nowIso(),
      });
      logGovernedMcp('app', 'quota_assignment_saved', {
        id: next.id,
        scope,
        scopeId: next.scopeId,
        clientId: next.clientId,
        resourceId: next.resourceId,
        month: next.month,
        tokenLimit: next.tokenLimit,
        status: next.status,
        renewsMonthly: next.renewsMonthly,
        resourceType: next.resourceType,
      });
      dispatch({ type: 'upsert_quota_assignment', item: next });
      persistLater('quota_assignment_saved', () =>
        governedMcpApi.upsertQuotaAssignment({
          id: next.id,
          scope: next.scope,
          scopeId: next.scopeId,
          clientId: next.clientId,
          resourceId: next.resourceId || next.clientId,
          month: next.month,
          tokenLimit: next.tokenLimit,
          status: next.status,
          name: next.name,
          renewsMonthly: next.renewsMonthly,
          resourceType: next.resourceType,
        })
      );
      return { ok: true };
    },
    [persistLater, state.published, state.quotaAssignments]
  );

  const deleteQuotaAssignment = useCallback(
    (id: string): { ok: true } | { ok: false; reason: string } => {
      const assignmentId = id.trim();
      if (!assignmentId) {
        logGovernedMcp('warning', 'quota_assignment_delete_blocked', { reason: 'missing_id' });
        return { ok: false, reason: 'Quota assignment is required.' };
      }
      const current = state.quotaAssignments.find((item) => item.id === assignmentId);
      if (!current) {
        logGovernedMcp('warning', 'quota_assignment_delete_blocked', { id: assignmentId, reason: 'missing' });
        return { ok: false, reason: 'That quota is no longer assigned.' };
      }
      logGovernedMcp('app', 'quota_assignment_deleted', {
        id: current.id,
        scope: current.scope,
        scopeId: current.scopeId,
        clientId: current.clientId,
        resourceId: current.resourceId,
        month: current.month,
        resourceType: current.resourceType,
      });
      dispatch({ type: 'delete_quota_assignment', id: assignmentId });
      persistLater('quota_assignment_deleted', () => governedMcpApi.deleteQuotaAssignment(assignmentId));
      return { ok: true };
    },
    [persistLater, state.quotaAssignments]
  );

  const recordActivity = useCallback((event: ActivityEvent) => {
    dispatch({ type: 'prepend_activity', event });
    persistLater('activity_recorded', () => governedMcpApi.recordActivity(event));
    const endpoint = state.endpoints.find((item) => item.id === event.endpointId);
    if (endpoint) {
      const next = {
        ...endpoint,
        requestVolume: endpoint.requestVolume + 1,
        lastActivityAt: event.timestamp,
        updatedAt: nowIso(),
      };
      dispatch({ type: 'upsert_endpoint', endpoint: next });
      persistLater('activity_endpoint', () => governedMcpApi.upsertEndpoint(next));
    }
    return event;
  }, [persistLater, state.endpoints]);

  const recordTestConnection = useCallback(
    (endpointId: string, clientId?: string) => {
      const endpoint = state.endpoints.find((item) => item.id === endpointId);
      const client = clientId
        ? state.clients.find((item) => item.id === clientId)
        : state.clients.find((item) => item.endpointId === endpointId && item.status === 'active');
      const enabled = Boolean(endpoint?.enabled);
      const decision: ActivityDecision = enabled ? 'allowed' : 'failed';
      const event: ActivityEvent = {
        id: createId('act'),
        timestamp: nowIso(),
        requestId: createId('req'),
        tenant: TENANT_NAME,
        userId: 'u-priya',
        userName: 'Priya Shah',
        groupNames: ['Risk Analytics'],
        roles: ['Administrator'],
        clientId: client?.id || 'unregistered',
        clientName: client?.name || 'Connection test (no client)',
        endpointId,
        endpointName: endpoint?.name || 'Unknown endpoint',
        operation: 'test_connection',
        capabilityId: null,
        capabilityName: 'MCP handshake',
        capabilityKind: null,
        representations: [],
        inputSummary: 'initialize → authenticate → list tools/resources/tasks',
        policiesEvaluated: ['Endpoint security switches'],
        decision,
        approvalStatus: 'not_required',
        dataSources: [],
        classificationsEncountered: [],
        recordsAccessed: 0,
        fieldsMasked: [],
        workflowRef: null,
        outputClassification: 'internal',
        durationMs: enabled ? 320 : 80,
        result: enabled
          ? 'Connection, authentication, governed discovery, and response succeeded.'
          : 'Endpoint is disabled. Handshake refused.',
        error: enabled ? null : 'endpoint_disabled',
        trace: buildTrace(decision, {
          Authentication: endpoint?.security.requireAuthenticatedUsers
            ? 'OIDC token validated against enterprise IdP.'
            : 'Anonymous test path (not recommended for production).',
          'Client Validation': client
            ? 'Registered client matched trust policy.'
            : 'No registered client; discovery limited to handshake.',
          'Capability Discovery/Invocation': 'Returned only capabilities the resolved user is authorized to see.',
          Authorization: enabled ? 'Endpoint enabled.' : 'Endpoint disabled.',
        }),
      };
      logGovernedMcp(enabled ? 'app' : 'warning', 'test_connection', { endpointId, decision });
      return recordActivity(event);
    },
    [recordActivity, state.clients, state.endpoints]
  );

  const simulateInvocation = useCallback(
    (input: {
      endpointId: string;
      clientId: string;
      publishedId: string;
      userId: string;
      operation?: ActivityEvent['operation'];
    }) => {
      const endpoint = state.endpoints.find((item) => item.id === input.endpointId);
      const client = state.clients.find((item) => item.id === input.clientId);
      const published = state.published.find((item) => item.id === input.publishedId);
      const user = DIRECTORY_USERS.find((item) => item.id === input.userId);
      const matchingPolicies = state.policies.filter(
        (policy) =>
          policy.status === 'active' &&
          (policy.endpointIds.length === 0 || policy.endpointIds.includes(input.endpointId)) &&
          (policy.clientIds.length === 0 || policy.clientIds.includes(input.clientId)) &&
          (policy.publishedCapabilityIds.length === 0 ||
            policy.publishedCapabilityIds.includes(input.publishedId))
      );

      let decision: ActivityDecision = 'allowed';
      let result = 'Governed execution completed.';
      let error: string | null = null;
      const deny = matchingPolicies.find((policy) => policy.effect === 'deny');
      const approve = matchingPolicies.find((policy) => policy.effect === 'require_approval');
      if (!endpoint?.enabled || client?.status !== 'active') {
        decision = 'failed';
        result = 'Client or endpoint is not active.';
        error = 'unavailable';
      } else if (deny) {
        decision = 'denied';
        result = `Denied by policy "${deny.name}".`;
        error = 'authorization_denied';
      } else if (published?.requiresApproval || approve) {
        decision = 'approval_required';
        result = 'Held for human approval. AI client cannot self-authorize.';
      } else if (
        published &&
        published.allowedClientIds.length > 0 &&
        !published.allowedClientIds.includes(input.clientId)
      ) {
        decision = 'denied';
        result = 'Capability is not published to this client.';
        error = 'authorization_denied';
      }

      const operation =
        input.operation ||
        (published?.capabilityKind === 'process'
          ? 'start_task'
          : published?.mcpRepresentations.includes('resource')
            ? 'read_resource'
            : 'invoke_tool');

      const event: ActivityEvent = {
        id: createId('act'),
        timestamp: nowIso(),
        requestId: createId('req'),
        tenant: TENANT_NAME,
        userId: input.userId,
        userName: user?.name || 'Unknown user',
        groupNames: DIRECTORY_GROUPS.filter((group) =>
          (published?.allowedGroupIds || []).includes(group.id)
        ).map((group) => group.name),
        roles: user ? [user.title] : [],
        clientId: input.clientId,
        clientName: client?.name || 'Unknown client',
        endpointId: input.endpointId,
        endpointName: endpoint?.name || 'Unknown endpoint',
        operation,
        capabilityId: published?.id || null,
        capabilityName: published?.mcpToolName || 'unknown',
        capabilityKind: published?.capabilityKind || null,
        representations: published?.mcpRepresentations || [],
        inputSummary: 'Simulated MCP request (synthetic).',
        policiesEvaluated: matchingPolicies.map((policy) => policy.name),
        decision,
        approvalStatus:
          decision === 'approval_required' ? 'pending' : decision === 'denied' ? 'not_applicable' : 'not_required',
        dataSources: published ? [published.sourceName] : [],
        classificationsEncountered: published ? [published.dataClassification] : [],
        recordsAccessed: decision === 'allowed' ? Math.min(published?.maxRecords || 1, 5) : 0,
        fieldsMasked: published?.maskingEnabled ? ['ssn_last4'] : [],
        workflowRef: published?.backedBy.includes('workflow') ? `${published.sourceName}` : null,
        outputClassification: published?.dataClassification || 'internal',
        durationMs: 180 + Math.floor(Math.random() * 400),
        result,
        error,
        trace: buildTrace(decision, {
          'User Resolution': `Mapped IdP subject to ${user?.name || 'no user'}. Client-supplied identity was ignored.`,
          'Client Validation': client
            ? `Trust policy: ${client.trustPolicy}.`
            : 'Unregistered client rejected.',
          Authorization: result,
        }),
      };
      logGovernedMcp(decision === 'allowed' ? 'app' : 'warning', 'simulated_invocation', {
        decision,
        capability: published?.mcpToolName,
      });
      return recordActivity(event);
    },
    [recordActivity, state.clients, state.endpoints, state.policies, state.published]
  );

  const resetDemo = useCallback(() => {
    logGovernedMcp('app', 'store_reload');
    void reload();
  }, [reload]);

  const effectiveQuotaUsers = useMemo(
    () =>
      buildEffectiveQuotaUsers(
        state.apiKeys,
        state.quotaAssignments,
        state.quotaUsages,
        quotaDirectory,
        currentQuotaMonth()
      ),
    [quotaDirectory, state.apiKeys, state.quotaAssignments, state.quotaUsages]
  );

  const viewState = useMemo(
    () => ({ ...state, quotaUsers: effectiveQuotaUsers }),
    [effectiveQuotaUsers, state]
  );

  const value = useMemo<GatewayContextValue>(
    () => ({
      state: viewState,
      ready,
      loading,
      loadError,
      persistError,
      reload,
      setTab,
      setConfigSection,
      setUsageSection,
      openConfiguration,
      setFilters,
      saveEndpoint,
      toggleEndpoint,
      rotateCredentials,
      deleteEndpoint,
      saveClient,
      setClientStatus,
      deleteClient,
      savePolicy,
      deletePolicy,
      savePublished,
      deletePublished,
      saveToolGroup,
      deleteToolGroup,
      saveApiKey,
      deleteApiKey,
      setQuotaAssignment,
      deleteQuotaAssignment,
      issueInferenceKey,
      quotaDirectory,
      recordTestConnection,
      simulateInvocation,
      resetDemo,
      catalogs: {
        users: DIRECTORY_USERS,
        groups: DIRECTORY_GROUPS,
        dataModels: DATA_MODELS,
        workflows: WORKFLOWS,
        externalMcp,
      },
    }),
    [
      viewState,
      ready,
      loading,
      loadError,
      persistError,
      reload,
      externalMcp,
      quotaDirectory,
      setTab,
      setConfigSection,
      setUsageSection,
      openConfiguration,
      setFilters,
      saveEndpoint,
      toggleEndpoint,
      rotateCredentials,
      deleteEndpoint,
      saveClient,
      setClientStatus,
      deleteClient,
      savePolicy,
      deletePolicy,
      savePublished,
      deletePublished,
      saveToolGroup,
      deleteToolGroup,
      saveApiKey,
      deleteApiKey,
      setQuotaAssignment,
      deleteQuotaAssignment,
      issueInferenceKey,
      recordTestConnection,
      simulateInvocation,
      resetDemo,
    ]
  );

  return <GatewayContext.Provider value={value}>{children}</GatewayContext.Provider>;
}

export function useGovernedMcp(): GatewayContextValue {
  const ctx = useContext(GatewayContext);
  if (!ctx) {
    throw new Error('useGovernedMcp must be used within GovernedMcpProvider');
  }
  return ctx;
}
